import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import PaginationWithState from "components/Pagination/PaginationWithState";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";

export default function WoColors() {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [woColorsData, setWoColorsData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [loading, setLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [previewImage, setPreviewImage] = useState(null);

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /sales-wo-colors?page=&limit=)
  const getWoColorsData = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/sales-wo-colors?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() },
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setWoColorsData(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, searchTerm, pageLimit, filterBy.value],
  );

  // Delete WoColors
  const deleteWoColorsData = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/sales-wo-color/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        await getWoColorsData();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          }),
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Adds Button Function
  const AddWoColorsData = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let _formData = new FormData();
      _formData.append("name", formData?.name || "");
      if (formData?.file) {
        _formData.append("file", formData?.file);
      }

      const { data } = await API.post("/sales-wo-color", _formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      if (data?.success) {
        await getWoColorsData({ page: 1 });
        // after add refresh current page (or go to first page)
        resetStates();
        // Refresh list
        setPageNumber(1);
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const handleUpdateWoColorsData = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      let _formData = new FormData();
      _formData.append("id", formData?.id);
      _formData.append("name", formData?.name || "");
      if (formData?.file) {
        _formData.append("file", formData?.file);
      }

      const { data } = await API.patch("/sales-wo-color", _formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      if (data?.success) {
        await getWoColorsData();
        resetStates();
        // refresh list
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const getBase64Image = async (e) => {
    const file = e.target.files[0];

    // const MAX_FILE_SIZE = 400 * 4000; // 800 KB in bytes
    // if (!["image/jpeg", "image/png"].includes(file.type)) {
    //     alert("Only JPG and PNG image files are allowed.");
    //     e.target.value = "";
    //     return;
    // }
    // if (file.size > MAX_FILE_SIZE) {
    //     alert(`File size exceeds limit (300 KB). Please select a smaller file.`);
    //     e.target.value = "";
    //     return;
    // }

    // Check image resolution
    const img = new Image();
    img.onload = () => {
      if (img.width !== 400 || img.height !== 400) {
        alert("Image must be exactly 400x400 pixels.");
        e.target.value = "";
        return;
      }

      setFormData((prev) => {
        return { ...prev, file };
      });
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setPreviewImage(reader?.result);
      };
    };
    img.src = URL.createObjectURL(file);
  };

  const getFileName = (fileName) => {
    return fileName?.split("/")[fileName?.split("/").length - 1];
  };

  // Sorting Up
  const sortUp = async (id) => {
    try {
      const { data } = await API.post(
        "/sales-wo-color-sort-up",
        { colorId: id },
        { headers: authHeader() },
      );
      if (data.success) {
        await getWoColorsData();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (id) => {
    try {
      const { data } = await API.post(
        "/sales-wo-color-sort-down",
        { colorId: id },
        { headers: authHeader() },
      );
      if (data.success) {
        await getWoColorsData();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateData = async (item) => {
    setFormData(item);
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  const resetStates = () => {
    setShowAddModal(false);
    setIsUpdateMode(false);
    setFormData({});
    setPreviewImage(null);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getWoColorsData(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber },
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy]);

  /*   Table functions
   ********************************************* */
  let filters = [
    { value: "name", name: "Name" },
    { value: "fileName", name: "File Name" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "ID", value: "colorId" },
    { name: "Name", value: "name" },
    { name: "File Name", value: "fileName" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item.colorId}</td>
      <td className="align-middle py-1">
        <div className="d-flex align-items-center gap-2">
          {item?.fileName && (
            <ImageWIthSignedUrl
              path={item.fileName}
              imgClasses={"rounded-3"}
              imgStyle={{
                width: 20,
                height: 20,
              }}
            />
          )}
          {item.name}
        </div>
      </td>
      <td className="align-middle py-1">{getFileName(item.fileName)}</td>
      <td className="align-middle py-1 text-end table-action">
        <button
          onClick={() => {
            sortUp(item.colorId);
          }}
          disabled={item.sortId === woColorsData?.length} // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-up p-0"></i>
        </button>
        <button
          onClick={() => {
            sortDown(item.colorId);
          }}
          disabled={item.sortId === 1} // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-down"></i>
        </button>
        <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>

        <DeleteModalButton
          btnClass={"btn btn-sm  px-1"}
          deleteFunc={deleteWoColorsData}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="p-2">
      {/* <h2 className="mb-3 ps-2">Colors</h2> */}
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {buttons()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getWoColorsData({ query: searchTerm, page: 1 });
              } else {
                getWoColorsData({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getWoColorsData({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          <div className="">
            <button
              onClick={() => {
                setShowAddModal(true);
                setFormData({});
                setIsUpdateMode(false);
              }}
              type="button"
              className="btn btn-primary  ms-1"
            >
              Add Colors
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 overflow-y-auto d-flex"
        style={{ height: "78vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={woColorsData}
            disableSorting={false}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/*   Add Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => resetStates()}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>{isUpdateMode ? "Update Color" : "Add Color"}</h4>
          </div>
          <hr className="mt-0" />
          <form
            onSubmit={isUpdateMode ? handleUpdateWoColorsData : AddWoColorsData}
            className=""
          >
            <div className="mb-3">
              <label className="">Color Name</label>
              <div className="">
                <input
                  type="text"
                  required
                  value={formData?.name || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Data Name"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="">Color File ( 400x400 px ) </label>
              <div className="">
                <input
                  type="file"
                  onChange={getBase64Image}
                  className="form-control"
                />
              </div>
              {previewImage ? (
                <div className="mt-2">
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ maxWidth: "100px", maxHeight: "100px" }}
                  />
                </div>
              ) : (
                isUpdateMode &&
                formData.fileName && (
                  <div className="mt-2">
                    <ImageWIthSignedUrl
                      imgStyle={{ maxWidth: "100px", maxHeight: "100px" }}
                      path={formData?.fileName}
                    />
                  </div>
                )
              )}
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    resetStates();
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
